import { AvailabilityObject, AvailabilitySlot } from '../types/schemas/abc';
import { formatInTimeZone } from 'date-fns-tz';

export const DAYS_SUNDAY_FIRST = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
export const DAYS_MONDAY_FIRST = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export const GRANULARITY = 30;
const MINUTES_IN_DAY = 24 * 60;

const getDayName = (dayIndex: number): keyof AvailabilityObject => {
  return DAYS_SUNDAY_FIRST[dayIndex] as keyof AvailabilityObject;
};

/**
 * Returns offset in minutes: how many minutes tz1 is ahead of tz2.
 * Uses formatInTimeZone to extract UTC offsets directly, avoiding the
 * fragile toLocaleString → new Date() round-trip.
 */
export const getTimezoneOffset = (tz1: string, tz2: string, at?: Date): number => {
  const ref = at ?? new Date();
  const offset1 = formatInTimeZone(ref, tz1, 'xxx'); // e.g. "+05:30"
  const offset2 = formatInTimeZone(ref, tz2, 'xxx');

  const parse = (str: string): number => {
    const sign = str[0] === '-' ? -1 : 1;
    const [h, m] = str.slice(1).split(':').map(Number);
    return sign * (h * 60 + m);
  };

  return parse(offset1) - parse(offset2);
};

/**
 * Shifts every slot in `availability` by `offset` minutes and returns
 * a new AvailabilityObject with slots placed on the correct days.
 *
 * Handles start rolling into next/previous day AND end crossing midnight
 * after start is normalised (splits into two slots).
 */
export const adjustAvailabilityForTimezone = (
  availability: AvailabilityObject,
  offset: number,
  dayOrder: readonly string[] = DAYS_SUNDAY_FIRST,
): AvailabilityObject => {
  const adjusted: AvailabilityObject = {
    sunday: [], monday: [], tuesday: [], wednesday: [],
    thursday: [], friday: [], saturday: [],
  };

  for (let i = 0; i < dayOrder.length; i++) {
    const day = dayOrder[i] as keyof AvailabilityObject;
    const slots = availability[day] || [];

    for (const slot of slots) {
      let start = slot.start + offset;
      let end = slot.end + offset;
      let dayIndex = i;

      // Normalise start into [0, MINUTES_IN_DAY)
      if (start >= MINUTES_IN_DAY) {
        start -= MINUTES_IN_DAY;
        end -= MINUTES_IN_DAY;
        dayIndex = (i + 1) % 7;
      } else if (start < 0) {
        start += MINUTES_IN_DAY;
        end += MINUTES_IN_DAY;
        dayIndex = (i + 6) % 7;
      }

      // After normalising start, end may still cross midnight → split
      if (end > MINUTES_IN_DAY) {
        const currentDay = dayOrder[dayIndex] as keyof AvailabilityObject;
        const nextDay = dayOrder[(dayIndex + 1) % 7] as keyof AvailabilityObject;
        adjusted[currentDay].push({ ...slot, start, end: MINUTES_IN_DAY });
        adjusted[nextDay].push({ ...slot, start: 0, end: end - MINUTES_IN_DAY });
      } else {
        const targetDay = dayOrder[dayIndex] as keyof AvailabilityObject;
        adjusted[targetDay].push({ ...slot, start, end });
      }
    }
  }

  return adjusted;
};

/**
 * Like adjustAvailabilityForTimezone but uses per-day offsets for DST accuracy.
 * `dayOffsets` is an array of 7 offsets in minutes, one per day in `dayOrder`.
 */
export const adjustAvailabilityPerDay = (
  availability: AvailabilityObject,
  dayOffsets: number[],
  dayOrder: readonly string[] = DAYS_SUNDAY_FIRST,
): AvailabilityObject => {
  const adjusted: AvailabilityObject = {
    sunday: [], monday: [], tuesday: [], wednesday: [],
    thursday: [], friday: [], saturday: [],
  };

  for (let i = 0; i < dayOrder.length; i++) {
    const day = dayOrder[i] as keyof AvailabilityObject;
    const slots = availability[day] || [];
    const offset = dayOffsets[i];

    for (const slot of slots) {
      let start = slot.start + offset;
      let end = slot.end + offset;
      let dayIndex = i;

      if (start >= MINUTES_IN_DAY) {
        start -= MINUTES_IN_DAY;
        end -= MINUTES_IN_DAY;
        dayIndex = (i + 1) % 7;
      } else if (start < 0) {
        start += MINUTES_IN_DAY;
        end += MINUTES_IN_DAY;
        dayIndex = (i + 6) % 7;
      }

      if (end > MINUTES_IN_DAY) {
        const currentDay = dayOrder[dayIndex] as keyof AvailabilityObject;
        const nextDay = dayOrder[(dayIndex + 1) % 7] as keyof AvailabilityObject;
        adjusted[currentDay].push({ ...slot, start, end: MINUTES_IN_DAY });
        adjusted[nextDay].push({ ...slot, start: 0, end: end - MINUTES_IN_DAY });
      } else {
        const targetDay = dayOrder[dayIndex] as keyof AvailabilityObject;
        adjusted[targetDay].push({ ...slot, start, end });
      }
    }
  }

  return adjusted;
};

/**
 * Total minutes of overlap between two (already tz-adjusted) availability objects.
 */
export const calculateOverlapMinutes = (
  avail1: AvailabilityObject,
  avail2: AvailabilityObject,
  dayOrder: readonly string[] = DAYS_SUNDAY_FIRST,
): number => {
  let total = 0;
  for (const day of dayOrder) {
    const d = day as keyof AvailabilityObject;
    for (const s1 of avail1[d] || []) {
      for (const s2 of avail2[d] || []) {
        const oStart = Math.max(s1.start, s2.start);
        const oEnd = Math.min(s1.end, s2.end);
        if (oStart < oEnd) total += oEnd - oStart;
      }
    }
  }
  return total;
};

/**
 * Unrolls availability into a Set of "day-minute" keys at GRANULARITY resolution.
 */
export const unrollAvailability = (availability: AvailabilityObject): Set<string> => {
  const slots = new Set<string>();
  for (const day in availability) {
    if (Object.prototype.hasOwnProperty.call(availability, day)) {
      (availability[day as keyof AvailabilityObject] || []).forEach((interval) => {
        const startMinute = Math.floor(interval.start / GRANULARITY) * GRANULARITY;
        for (let minute = startMinute; minute < interval.end; minute += GRANULARITY) {
          slots.add(`${day}-${minute}`);
        }
      });
    }
  }
  return slots;
};

/**
 * Adds or removes an entire recurring series from the availability object.
 * @param currentAvailability The user's existing availability object.
 * @param action Whether to 'add' or 'remove' a series.
 * @param details The specifics of the series to modify.
 * @returns The new, updated availability object.
 */
export const updateAvailabilitySeries = (
  currentAvailability: AvailabilityObject,
  action: 'add' | 'remove',
  details: {
    id: string; 
    startTime: number;
    endTime: number;
    dayOfWeek?: number; 
    repeat: 'Daily' | 'Weekly' | 'Every weekday (Mon – Fri)';
  }
): AvailabilityObject => {
  const newAvailability = JSON.parse(JSON.stringify(currentAvailability));
  const { id, startTime, endTime, dayOfWeek, repeat } = details;
  if (action === 'remove') {
    for (let i = 0; i < 7; i++) {
      const dayName = getDayName(i);
      newAvailability[dayName] = (newAvailability[dayName] || []).filter(
        (slot: AvailabilitySlot) => slot.id !== id
      );
    }
    return newAvailability;
  }
  
  if (action === 'add') {
    const newSlot: Omit<AvailabilitySlot, 'id'> = { start: startTime, end: endTime, repeat };
    
    const applyToDay = (dayIndex: number) => {
      const dayName = getDayName(dayIndex);
      if (!newAvailability[dayName]) newAvailability[dayName] = [];
      newAvailability[dayName].push({ ...newSlot, id });
    };

    switch (repeat) {
      case 'Weekly':
        if (dayOfWeek === undefined) throw new Error('dayOfWeek is required');
        applyToDay(dayOfWeek);
        break;
      case 'Daily':
        for (let i = 0; i < 7; i++) applyToDay(i);
        break;
      case 'Every weekday (Mon – Fri)':
        for (let i = 1; i <= 5; i++) applyToDay(i);
        break;
    }
    return newAvailability;
  }
  
  return currentAvailability;
};